import type { RouteRecordName } from 'vue-router'

/**
 * 路由间的一次性数据交接（handoff）。
 *
 * 适用场景：B 页面返回 A 页面时，想把一些状态带回给 A，但又不方便用 query 传递。
 * 典型情况是用 router.back() 返回 —— back() 会沿用 A 在历史记录里的旧 URL，
 * 无法通过 query 传参；而改用 push/replace 到 A 又会破坏历史栈
 * （replace 会让 A 在历史里出现两次，用户需要多点一次返回才能退回上一级）。
 *
 * 用法：
 * ```ts
 * // 离开 B 之前：把数据交接给 A，然后正常 back()
 * setRouteHandoff(RouteNames.A, { tab: 'residenceInfo' })
 * router.back()
 *
 * // A 的 onMounted：读取（读取即清空），没有则走原有默认逻辑
 * const handoff = consumeRouteHandoff<{ tab: string }>(RouteNames.A)
 * ```
 *
 * 语义约定：
 * - 同一时刻只保留最近一次交接数据，重复调用后者覆盖前者；
 * - 读取即清空，避免影响之后从其他入口进入目标页；
 * - 若导航到的不是目标页，交接数据立即失效（见 clearRouteHandoffOnNavigate，
 *   已挂在路由 afterEach 上），避免用户中途改变去向后残留脏数据；
 * - 只存在内存中，刷新页面即丢失，因此目标页必须能在没有交接数据时正常工作。
 */
interface RouteHandoff {
  target: string
  payload: unknown
}

let pendingHandoff: RouteHandoff | null = null

/** 把数据交接给目标路由（routeName 建议使用 RouteNames 常量） */
export const setRouteHandoff = <T>(routeName: string, payload: T): void => {
  pendingHandoff = { target: routeName, payload }
}

/** 目标路由读取交接数据，读取后立即清空；没有则返回 undefined */
export const consumeRouteHandoff = <T>(routeName: string): T | undefined => {
  if (!pendingHandoff || pendingHandoff.target !== routeName) return undefined
  const { payload } = pendingHandoff
  pendingHandoff = null
  return payload as T
}

/**
 * 导航完成后清理「没有落到目标路由」的交接数据。
 * 由 router.afterEach 统一调用，业务代码无需关心。
 */
export const clearRouteHandoffOnNavigate = (routeName?: RouteRecordName | null): void => {
  if (!pendingHandoff) return
  if (routeName == null || String(routeName) !== pendingHandoff.target) {
    pendingHandoff = null
  }
}
